import { Button } from "@/src/components/buttons";
import { Card } from "@/src/components/cards";
import { openDrawer, closeDrawer } from "@/src/components/dialogs";
import {
  Appointment,
  appointmentStatuses,
  AppointmentType,
  Patient,
  patientItemStatuses,
  Service,
  SimpleExam,
} from "@/src/data/allTypes";
import { allItemTypes } from "@/src/data/data";
import apiFetch from "@/src/utils/apiFetch";
import {
  formatDate,
  getItemId,
  getServiceId,
  getUserId,
  isCabinet,
  uid,
} from "@/src/utils/utils";
import { openAppointmentDemandForm } from "../appointments/appointmentsUtils";
import { ColorCircle } from "@/src/components/colorCircle";
import { Input } from "@/src/components/universalInput";
import { UploadSpots } from "@/src/components/uploadSpots";
import { useState } from "react";
import { CONFIG } from "@/src/data/globalVariables";

export function ExamDemandButton({
  patient,
  exam,
  allServices,
  allAppointments,
  isHospitalized,
  onChange,
}: {
  patient: Patient;
  exam: SimpleExam;
  allServices: Service[];
  allAppointments: Appointment[];
  isHospitalized: boolean;
  onChange: (exam: SimpleExam) => void;
}) {
  let examStatusInfo = patientItemStatuses[exam.status || "pending"];

  let examStatus = exam.status;
  let appointment = allAppointments.find(
    (appointment) => appointment.item == exam?.id
  );

  if (appointment) {
    let statusInfo = appointmentStatuses[appointment.status || "pending"];
    let itemType = allItemTypes[statusInfo?.type];
    return (
      <Card color={itemType?.id as any} leading={<div></div>}>
        <div className="tcenter" style={{ color: itemType?.color }}>
          {statusInfo?.name}
        </div>
        <div className="tcenter gray">{appointment.service?.name}</div>
        {appointment.start && (
          <div className="tcenter">
            {" "}
            le <strong>{formatDate(appointment.start, "LL à HH:mm")}</strong>
          </div>
        )}
      </Card>
    );
  }

  if (
    exam.reference?.category != "imaging" &&
    getItemId(exam.reference) != getItemId(CONFIG.get()?.anapathExam)
  )
    return null;

  if (examStatus == "finished") return null;

  let services = allServices.filter((service) =>
    service?.appointmentTypes?.some(
      (type) => getItemId(type?.exam) == exam?.reference?.id
    )
  );

  if (services.length == 0 || isCabinet()) return null;

  return (
    <Button
      color="purple"
      type={"tertiary"}
      onClick={async () => {
        await openExamDemandDrawer({
          exam,
          isHospitalized: true,
          patient,
          services,
          onConfirm: async (appointment) => {
            onChange(exam);
          },
        });
      }}
    >
      Envoyer une demande
    </Button>
  );
}

export async function openExamDemandDrawer({
  services,
  patient,
  exam,
  isHospitalized,
  onConfirm,
}: {
  services: Service[];
  patient: Patient;
  exam: SimpleExam;
  isHospitalized: boolean;
  onConfirm?: (appointment: Appointment) => void;
}) {
  let specificServices =
    services.filter((service) =>
      service?.appointmentTypes?.some(
        (type) => getItemId(type?.exam) == getItemId(exam?.reference)
      )
    ) || [];

  async function openServiceDemand(service: Service | null | undefined) {
    if (!service) throw new Error("Service not found");
    let appointmentTypes = service?.appointmentTypes?.filter(
      (type) => getItemId(type?.exam) == getItemId(exam?.reference)
    );

    let appointment: Appointment = {
      doctor: null,
      patient,
      service: getItemId(service),
      demandService: getServiceId(),
      start: null,
      end: null,
      anchorDate: null,

      item: getItemId(exam),
      status: "pending",
      type: appointmentTypes?.[0],
      isHospitalized,
      creator: getUserId(),
    };

    await openDrawer({
      id: "examDemandAppointmentForm",
      title: "Demande",
      additionalClasses: ["small"],
      content: (
        <ExamDemandAppointmentForm
          patient={patient}
          appointmentTypes={appointmentTypes || []}
          current={appointment}
          onChange={(newValue) => {
            appointment = newValue;
          }}
        ></ExamDemandAppointmentForm>
      ),
      onConfirm: async () => {
        appointment = await openAppointmentDemandForm(appointment);

        appointment = await apiFetch(
          "appointments",
          "addOne",
          {
            appointment,
          },
          "Erreur lors de l'enregistrement de la demande"
        );
        console.log("gonna save appointment", appointment);

        apiFetch("appointments", "changeAppointmentStatus", {
          appointmentId: appointment.id,
          status: appointment.status,
          userId: getUserId(),
        });

        closeDrawer("examDemandDrawer");
        onConfirm?.(appointment);
      },
    });
  }

  await openDrawer({
    id: "examDemandDrawer",
    title: "Demander un examen",
    additionalClasses: ["medium"],
    content: (
      <div className=" c1 gap10">
        {specificServices.map((service) => {
          return (
            <div
              key={uid()}
              className="card"
              onClick={async () => {
                await openServiceDemand(service);
              }}
            >
              <div className="bold">{service.name}</div>
            </div>
          );
        })}
      </div>
    ),
  });
}

function ExamDemandAppointmentForm({
  patient,
  appointmentTypes,
  current,
  onChange,
}: {
  patient: Patient;
  appointmentTypes: (AppointmentType | null | undefined)[];
  current?: Appointment;
  onChange: (newValue: Appointment) => void;
}) {
  let [appointment, setAppointment] = useState(current || ({} as Appointment));
  function setNewValue(newValue: Appointment) {
    setAppointment(newValue);
    onChange(newValue);
  }

  return (
    <div className="grid c1 gap20">
      <div className="field">
        <div className="label">Type de rendez-vous</div>
        <div className="gric c2 gap10">
          {appointmentTypes?.map((type) => {
            if (!type) return;
            let isSelected = appointment.type?.id == type.id;
            return (
              <div
                key={uid()}
                className="card grid c1 gap10"
                style={{
                  borderColor: isSelected ? type.color : "white",
                  borderWidth: "3px",
                  borderStyle: "solid",
                }}
                onClick={() => {
                  setNewValue({ ...appointment, type });
                }}
              >
                <div className="cauto-1 acenter gap10">
                  <ColorCircle color={type.color}></ColorCircle>
                  {type.name}
                </div>
              </div>
            );
          })}
        </div>
      </div>

      <UploadSpots
        name="Pièces jointes"
        value={appointment.uploads}
        additionalUploadData={{
          patient: getItemId(patient),
        }}
        gridCount={2}
        spotsCount={2}
        onChange={(newValue) => {
          setNewValue({
            ...appointment,
            uploads: newValue,
          });
        }}
      ></UploadSpots>

      <Input
        name={"Urgent ?"}
        value={appointment.isUrgent == true ? "true" : "false"}
        type="chips"
        options={[
          { label: "Oui", value: "true" },
          { label: "Non", value: "false" },
        ]}
        onChange={(newValue) => {
          setNewValue({ ...appointment, isUrgent: newValue == "true" });
        }}
      ></Input>

      <Input
        name={"Notes"}
        value={appointment.notes}
        type="textarea"
        rows={5}
        onChange={(newValue) => {
          setNewValue({ ...appointment, notes: newValue });
        }}
      ></Input>
    </div>
  );
}
